import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import chatbotImage from '../../assets/img/home/chatbot.svg';
import { useDrawer } from '../../contexts/DrawerContext';

interface AIChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  showGrievanceButton?: boolean;
  showTaxOptions?: boolean;
  showPropertyTaxButton?: boolean;
  showWaterTaxButton?: boolean;
  showTradeLicenseOptions?: boolean;
  showBookingOptions?: boolean;
}

interface QuickAction {
  label: string;
  labelTe: string;
  query: string;
}

const quickActions: QuickAction[] = [
  { label: 'Tax Payment', labelTe: 'పన్ను చెల్లింపు', query: 'I want to pay tax' },
  { label: 'File Grievance', labelTe: 'ఫిర్యాదు', query: 'I want to file a grievance' },
  { label: 'Trade License', labelTe: 'వ్యాపార లైసెన్స్', query: 'I need trade license' },
  { label: 'Track Complaint', labelTe: 'ఫిర్యాదు ట్రాక్', query: '__TRACK_COMPLAINT__' },
  { label: 'Contact RMC', labelTe: 'సంప్రదించండి', query: 'How can I contact RMC?' },
];

// Knowledge base for RMC services
const knowledgeBase: { [key: string]: { en: string; te: string } } = {
  property_tax: {
    en: 'Click below to pay your Property Tax online. You will be redirected to the official RMC Property Tax portal.',
    te: 'మీ ఆస్తి పన్నును ఆన్‌లైన్‌లో చెల్లించడానికి క్రింద క్లిక్ చేయండి. మీరు అధికారిక RMC ఆస్తి పన్ను పోర్టల్‌కు రీడైరెక్ట్ చేయబడతారు.'
  },
  water_tax: {
    en: 'Click below to pay your Water Tax online. You will be redirected to the official RMC Water Tax portal.',
    te: 'మీ నీటి పన్నును ఆన్‌లైన్‌లో చెల్లించడానికి క్రింద క్లిక్ చేయండి. మీరు అధికారిక RMC నీటి పన్ను పోర్టల్‌కు రీడైరెక్ట్ చేయబడతారు.'
  },
  tax_options: {
    en: 'Which tax payment would you like to make? Please select an option below:',
    te: 'మీరు ఏ పన్ను చెల్లింపు చేయాలనుకుంటున్నారు? దయచేసి దిగువ ఎంపికను ఎంచుకోండి:'
  },
  grievance: {
    en: 'I can help you file a grievance right now! Click the button below to select your complaint category and submit your grievance.',
    te: 'నేను మీకు ఇప్పుడే ఫిర్యాదు దాఖలు చేయడంలో సహాయం చేయగలను! మీ ఫిర్యాదు వర్గాన్ని ఎంచుకుని సమర్పించడానికి దిగువ బటన్‌ను క్లిక్ చేయండి.'
  },
  trade_license: {
    en: 'What would you like to do with Trade License? Please select an option below:',
    te: 'వ్యాపార లైసెన్స్ విషయంలో మీరు ఏమి చేయాలనుకుంటున్నారు? దయచేసి దిగువ ఎంపికను ఎంచుకోండి:'
  },
  birth_certificate: {
    en: 'To get a Birth Certificate:\n\n1. Login to your citizen account\n2. Go to Services > Birth & Death Certificates\n3. Select "Birth Certificate"\n4. Fill in the required details\n5. Upload supporting documents\n6. Pay the applicable fee\n7. Submit your application',
    te: 'జనన ధృవీకరణ పత్రం పొందడానికి:\n\n1. మీ పౌర ఖాతాలోకి లాగిన్ అవ్వండి\n2. సేవలు > జనన & మరణ ధృవీకరణ పత్రాలు కు వెళ్ళండి\n3. "జనన ధృవీకరణ పత్రం" ఎంచుకోండి\n4. అవసరమైన వివరాలను నింపండి\n5. సహాయక పత్రాలను అప్‌లోడ్ చేయండి\n6. ఫీజు చెల్లించండి\n7. దరఖాస్తును సమర్పించండి'
  },
  contact: {
    en: 'Contact RMC:\n\n📍 Address: Municipal Corporation Rajamahendravaram MCR, Near Gokavaram Bus Stand, Sheshyametta, Rajamahendravaram.\n\n📞 Phone: 9494 060 060\n\n📧 Email: comm_mcr@yahoo.co.in\n\n🕐 Office Hours: Mon-Fri, 10:00 AM - 5:00 PM',
    te: 'RMCని సంప్రదించండి:\n\n📍 చిరునామా: మునిసిపల్ కార్పొరేషన్ రాజమహేంద్రవరం MCR, గోకవరం బస్ స్టాండ్ సమీపంలో, శేషయ్యమెట్ట, రాజమహేంద్రవరం.\n\n📞 ఫోన్: 9494 060 060\n\n📧 ఇమెయిల్: comm_mcr@yahoo.co.in\n\n🕐 కార్యాలయ సమయాలు: సోమ-శుక్ర, 10:00 AM - 5:00 PM'
  },
  tourism: {
    en: 'Rajahmundry Tourism:\n\n🌉 Godavari Bridge\n🛕 Kotilingeshwara Temple\n🌸 ISKCON Temple\n🎨 Rallabandi Subba Rao Museum\n🌿 Kadiyam Nurseries\n\nVisit our Tourism section for more details!',
    te: 'రాజమహేంద్రవరం పర్యాటకం:\n\n🌉 గోదావరి వంతెన\n🛕 కోటిలింగేశ్వర ఆలయం\n🌸 ఇస్కాన్ ఆలయం\n🎨 రల్లబండి సుబ్బారావు మ్యూజియం\n🌿 కాడియం నర్సరీలు\n\nమరిన్ని వివరాల కోసం మా పర్యాటక విభాగాన్ని సందర్శించండి!'
  },
  default: {
    en: 'I can help you with:\n\n• Property Tax & Water Tax payments\n• Filing grievances and complaints\n• Birth/Death/Marriage certificates\n• Trade License applications\n• Hall and Arena bookings\n• Tourism information\n• Contact details\n\nPlease ask your question!',
    te: 'నేను మీకు సహాయం చేయగలను:\n\n• ఆస్తి పన్ను & నీటి పన్ను చెల్లింపులు\n• ఫిర్యాదులు దాఖలు చేయడం\n• జనన/మరణ/వివాహ ధృవీకరణ పత్రాలు\n• వ్యాపార లైసెన్స్ దరఖాస్తులు\n• హాల్ మరియు అరేనా బుకింగ్‌లు\n• పర్యాటక సమాచారం\n• సంప్రదింపు వివరాలు\n\nదయచేసి మీ ప్రశ్నను అడగండి!'
  }
};

interface ResponseType {
  text: string;
  isGrievance: boolean;
  showTaxOptions: boolean;
  showPropertyTax: boolean;
  showWaterTax: boolean;
  showTradeLicenseOptions: boolean;
  showBookingOptions: boolean;
}

function getResponse(query: string, language: string): ResponseType {
  const lowerQuery = query.toLowerCase();
  const defaultResponse: ResponseType = { text: '', isGrievance: false, showTaxOptions: false, showPropertyTax: false, showWaterTax: false, showTradeLicenseOptions: false, showBookingOptions: false };

  // Check for specific property tax query
  if ((lowerQuery.includes('property') && !lowerQuery.includes('water')) || lowerQuery.includes('ఆస్తి')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.property_tax.en : knowledgeBase.property_tax.te, showPropertyTax: true };
  }
  // Check for specific water tax query
  if ((lowerQuery.includes('water') && !lowerQuery.includes('property')) || lowerQuery.includes('నీటి') || lowerQuery.includes('నీరు')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.water_tax.en : knowledgeBase.water_tax.te, showWaterTax: true };
  }
  // Check for general tax/payment query - show both options
  if (lowerQuery.includes('tax') || lowerQuery.includes('payment') || lowerQuery.includes('pay') || lowerQuery.includes('పన్ను') || lowerQuery.includes('చెల్లింపు')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.tax_options.en : knowledgeBase.tax_options.te, showTaxOptions: true };
  }
  // Check for trade license query
  if (lowerQuery.includes('trade') || lowerQuery.includes('license') || lowerQuery.includes('licence') || lowerQuery.includes('వ్యాపార') || lowerQuery.includes('లైసెన్స్')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.trade_license.en : knowledgeBase.trade_license.te, showTradeLicenseOptions: true };
  }
  if (lowerQuery.includes('grievance') || lowerQuery.includes('complaint') || lowerQuery.includes('ఫిర్యాదు') || lowerQuery.includes('file') || lowerQuery.includes('report') || lowerQuery.includes('issue') || lowerQuery.includes('problem')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.grievance.en : knowledgeBase.grievance.te, isGrievance: true };
  }
  if (lowerQuery.includes('birth') || lowerQuery.includes('జనన')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.birth_certificate.en : knowledgeBase.birth_certificate.te };
  }
  if (lowerQuery.includes('contact') || lowerQuery.includes('phone') || lowerQuery.includes('address') || lowerQuery.includes('సంప్రదింప')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.contact.en : knowledgeBase.contact.te };
  }
  if (lowerQuery.includes('tourism') || lowerQuery.includes('visit') || lowerQuery.includes('places') || lowerQuery.includes('పర్యాటక')) {
    return { ...defaultResponse, text: language === 'en' ? knowledgeBase.tourism.en : knowledgeBase.tourism.te };
  }
  // Check for booking queries
  if (lowerQuery.includes('hall') && lowerQuery.includes('arena') && lowerQuery.includes('booking') || 
      lowerQuery.includes('booking') && (lowerQuery.includes('hall') || lowerQuery.includes('arena'))) {
    return {
      ...defaultResponse,
      text: language === 'en'
        ? 'I can help you with Hall and Arena bookings. Please select an option below:'
        : 'నేను మీకు హాల్ మరియు అరేనా బుకింగ్‌లతో సహాయం చేయగలను. దయచేసి దిగువ ఎంపికను ఎంచుకోండి:',
      showBookingOptions: true
    };
  }
  if (lowerQuery.includes('hello') || lowerQuery.includes('hi') || lowerQuery.includes('హలో') || lowerQuery.includes('నమస్తే')) {
    return {
      ...defaultResponse,
      text: language === 'en'
        ? 'Hello! Welcome to RMC AI Assistant. How can I help you today?'
        : 'హలో! RMC AI అసిస్టెంట్‌కు స్వాగతం. నేను మీకు ఎలా సహాయం చేయగలను?'
    };
  }
  if (lowerQuery.includes('thank') || lowerQuery.includes('ధన్యవాద')) {
    return {
      ...defaultResponse,
      text: language === 'en'
        ? 'You\'re welcome! Is there anything else I can help you with?'
        : 'మీకు స్వాగతం! నేను మీకు మరేదైనా సహాయం చేయగలనా?'
    };
  }

  return { ...defaultResponse, text: language === 'en' ? knowledgeBase.default.en : knowledgeBase.default.te };
}

export default function AIChatDrawer({ isOpen, onClose, currentLanguage }: AIChatDrawerProps) {
  const { openDrawer } = useDrawer();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [language, setLanguage] = useState<'en' | 'te'>(currentLanguage === 'te' ? 'te' : 'en');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Initialize with welcome message when drawer opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Check if booking options should be shown
      const showBookingOptions = localStorage.getItem('showBookingOptions') === 'true';
      
      if (showBookingOptions) {
        // Clear the flag
        localStorage.removeItem('showBookingOptions');
        
        // Add user message
        const userMessage: Message = {
          id: generateId(),
          text: language === 'en' ? 'Hall and Arena bookings' : 'హాల్ మరియు అరేనా బుకింగ్‌లు',
          sender: 'user',
          timestamp: new Date(),
        };
        
        // Add bot response with booking options
        const bookingMessage: Message = {
          id: generateId(),
          text: language === 'en'
            ? 'I can help you with Hall and Arena bookings. Please select an option below:'
            : 'నేను మీకు హాల్ మరియు అరేనా బుకింగ్‌లతో సహాయం చేయగలను. దయచేసి దిగువ ఎంపికను ఎంచుకోండి:',
          sender: 'bot',
          timestamp: new Date(),
          showBookingOptions: true,
        };
        
        setMessages([userMessage, bookingMessage]);
      } else {
        const welcomeMessage: Message = {
          id: 'welcome',
          text: language === 'en'
            ? 'Hello! I\'m RMC AI Assistant. I can help you with information about RMC services, payments, grievances, certificates, and more. How can I assist you today?'
            : 'హలో! నేను RMC AI అసిస్టెంట్. RMC సేవలు, చెల్లింపులు, ఫిర్యాదులు, సర్టిఫికేట్‌లు గురించి నేను మీకు సహాయం చేయగలను. నేను మీకు ఎలా సహాయం చేయగలను?',
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
      }
    }
  }, [isOpen, language]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (text?: string) => {
    const messageText = text || inputValue.trim();
    if (!messageText) return;

    const userMessage: Message = {
      id: generateId(),
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking delay
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 800));

    const response = getResponse(messageText, language);

    const botMessage: Message = {
      id: generateId(),
      text: response.text,
      sender: 'bot',
      timestamp: new Date(),
      showGrievanceButton: response.isGrievance,
      showTaxOptions: response.showTaxOptions,
      showPropertyTaxButton: response.showPropertyTax,
      showWaterTaxButton: response.showWaterTax,
      showTradeLicenseOptions: response.showTradeLicenseOptions,
      showBookingOptions: response.showBookingOptions,
    };

    setIsTyping(false);
    setMessages(prev => [...prev, botMessage]);
  };

  const handleQuickAction = (action: QuickAction) => {
    if (action.query === '__TRACK_COMPLAINT__') {
      handleTrackComplaint();
      return;
    }
    handleSendMessage(action.query);
  };

  const handleTrackComplaint = () => {
    setMessages([]);
    setInputValue('');
    onClose();
    setTimeout(() => {
      openDrawer('complaint-status');
    }, 100);
  };

  const handleFileGrievance = () => {
    setMessages([]);
    setInputValue('');
    onClose();
    // Small delay to allow drawer to close before opening new one
    setTimeout(() => {
      openDrawer('complaint-category');
    }, 100);
  };

  const handlePropertyTax = () => {
    window.open('https://rajahmundry.emunicipal.ap.gov.in/ptis/citizen/search/search-searchForm.action#no-back-button', '_blank');
  };

  const handleWaterTax = () => {
    window.open('https://rajahmundry.emunicipal.ap.gov.in/wtms/search/waterSearch/', '_blank');
  };

  const handleNewTradeLicense = () => {
    window.open('https://rajahmundry.emunicipal.ap.gov.in/tl/newtradelicense/newTradeLicense-newForm.action#no-back', '_blank');
  };

  const handleRenewalTradeLicense = () => {
    window.open('https://rajahmundry.emunicipal.ap.gov.in/tl/pay/online#no-back', '_blank');
  };

  const handleFunctionHallBooking = () => {
    onClose();
    navigate('/function-hall-booking');
  };

  const handleArenaBooking = () => {
    onClose();
    navigate('/arena-booking');
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'te' : 'en');
  };

  const handleClose = () => {
    setMessages([]);
    setInputValue('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[10000]"
            onClick={handleClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white px-4 py-4 flex items-center justify-between shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/95 shadow-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={chatbotImage}
                    alt="RMC AI"
                    className="w-[78%] h-[78%] object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">
                    {language === 'en' ? 'RMC AI Assistant' : 'RMC AI అసిస్టెంట్'}
                  </h3>
                  <p className="text-xs text-white/80">
                    {language === 'en' ? 'Ask anything about RMC' : 'RMC గురించి ఏదైనా అడగండి'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleLanguage}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors"
                >
                  {language === 'en' ? 'తెలుగు' : 'EN'}
                </button>
                <button
                  onClick={handleClose}
                  className="text-white hover:text-gray-100 transition p-1"
                  aria-label="Close chat"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-start gap-3 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.sender === 'bot' && (
                    <div className="w-8 h-8 rounded-full bg-white border border-orange-200 flex items-center justify-center flex-shrink-0 shadow">
                      <img
                        src={chatbotImage}
                        alt="RMC AI"
                        className="w-6 h-6 object-contain"
                      />
                    </div>
                  )}
                  <div className={`max-w-[75%] ${message.sender === 'user' ? 'order-1' : ''}`}>
                    {message.sender === 'bot' && (
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-orange-600">RMC AI</span>
                      </div>
                    )}
                    <div
                      className={`rounded-lg px-4 py-2 shadow-sm ${
                        message.sender === 'bot'
                          ? 'bg-white border border-gray-200'
                          : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                      {message.showGrievanceButton && (
                        <motion.button
                          onClick={handleFileGrievance}
                          className="mt-3 w-full px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <i className="ri-file-list-3-line"></i>
                          {language === 'en' ? 'File Grievance Now' : 'ఇప్పుడు ఫిర్యాదు దాఖలు చేయండి'}
                        </motion.button>
                      )}
                      {/* Tax Options - Show both buttons */}
                      {message.showTaxOptions && (
                        <div className="mt-3 flex flex-col gap-2">
                          <motion.button
                            onClick={handlePropertyTax}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <i className="ri-home-line"></i>
                            {language === 'en' ? 'Property Tax' : 'ఆస్తి పన్ను'}
                          </motion.button>
                          <motion.button
                            onClick={handleWaterTax}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <i className="ri-drop-line"></i>
                            {language === 'en' ? 'Water Tax' : 'నీటి పన్ను'}
                          </motion.button>
                        </div>
                      )}
                      {/* Property Tax Only Button */}
                      {message.showPropertyTaxButton && (
                        <motion.button
                          onClick={handlePropertyTax}
                          className="mt-3 w-full px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <i className="ri-home-line"></i>
                          {language === 'en' ? 'Pay Property Tax' : 'ఆస్తి పన్ను చెల్లించండి'}
                        </motion.button>
                      )}
                      {/* Water Tax Only Button */}
                      {message.showWaterTaxButton && (
                        <motion.button
                          onClick={handleWaterTax}
                          className="mt-3 w-full px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <i className="ri-drop-line"></i>
                          {language === 'en' ? 'Pay Water Tax' : 'నీటి పన్ను చెల్లించండి'}
                        </motion.button>
                      )}
                      {/* Trade License Options */}
                      {message.showTradeLicenseOptions && (
                        <div className="mt-3 flex flex-col gap-2">
                          <motion.button
                            onClick={handleNewTradeLicense}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <i className="ri-add-circle-line"></i>
                            {language === 'en' ? 'Apply New Trade License' : 'కొత్త వ్యాపార లైసెన్స్ దరఖాస్తు'}
                          </motion.button>
                          <motion.button
                            onClick={handleRenewalTradeLicense}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <i className="ri-refresh-line"></i>
                            {language === 'en' ? 'Renewal Trade License' : 'వ్యాపార లైసెన్స్ పునరుద్ధరణ'}
                          </motion.button>
                        </div>
                      )}
                      {/* Booking Options */}
                      {message.showBookingOptions && (
                        <div className="mt-3 flex flex-col gap-2">
                          <motion.button
                            onClick={handleFunctionHallBooking}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <i className="ri-building-2-line"></i>
                            {language === 'en' ? 'Function Hall Booking' : 'ఫంక్షన్ హాల్ బుకింగ్'}
                          </motion.button>
                          <motion.button
                            onClick={handleArenaBooking}
                            className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 shadow-md"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <i className="ri-calendar-event-line"></i>
                            {language === 'en' ? 'Arena Booking' : 'అరీనా బుకింగ్'}
                          </motion.button>
                        </div>
                      )}
                    </div>
                    <p className={`text-xs text-gray-500 mt-1 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                      {message.timestamp.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {message.sender === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0 shadow">
                      <i className="ri-user-line text-white text-sm"></i>
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-white border border-orange-200 flex items-center justify-center flex-shrink-0 shadow">
                    <img
                      src={chatbotImage}
                      alt="RMC AI"
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <motion.span
                        className="w-2 h-2 bg-gray-400 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                      />
                      <motion.span
                        className="w-2 h-2 bg-gray-400 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                      />
                      <motion.span
                        className="w-2 h-2 bg-gray-400 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 1 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-600 mb-2 font-medium">
                  {language === 'en' ? 'Quick Actions:' : 'త్వరిత చర్యలు:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(action)}
                      className="px-3 py-1.5 bg-white border border-orange-200 rounded-full text-xs font-medium text-orange-600 hover:bg-orange-50 hover:border-orange-300 transition-colors shadow-sm"
                    >
                      {language === 'en' ? action.label : action.labelTe}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="border-t border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isTyping) {
                      handleSendMessage();
                    }
                  }}
                  placeholder={language === 'en' ? 'Type your question...' : 'మీ ప్రశ్నను టైప్ చేయండి...'}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  disabled={isTyping}
                />
                <motion.button
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim() || isTyping}
                  className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="ri-send-plane-fill text-xl"></i>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
